import React from 'react';
import { format } from 'date-fns';
import { AlertCircle, AlertTriangle, Users } from 'lucide-react';
import { LessonWaitlistSection } from '../lessons/LessonWaitlistSection';
import type { LessonOccurrence } from './LessonCalendarView';

interface LessonOccurrenceDayPopoverProps {
  occurrence: LessonOccurrence;
  hasCoach: boolean;
  coaches: any[];
  hasCoachAvailabilityWarning?: boolean;
}

export function LessonOccurrenceDayPopover({
  occurrence,
  hasCoach,
  coaches,
  hasCoachAvailabilityWarning = false,
}: LessonOccurrenceDayPopoverProps) {
  const { lesson, date, status } = occurrence;
  const coachNames = lesson.coachIds
    ?.map((id) => coaches.find((c) => c.id === id)?.firstName)
    .filter(Boolean)
    .join(', ') || 'N/A';

  const currentEnrollment = lesson.currentEnrollment || 0;
  const maxCapacity = lesson.maxCapacity;
  const isFull = maxCapacity !== -1 && currentEnrollment >= maxCapacity;
  const enrollmentPercentage = maxCapacity === -1 ? 0 : (currentEnrollment / maxCapacity) * 100;

  return (
    <div className="space-y-3">
      <div>
        <h4 className="font-semibold">{lesson.name}</h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {format(date, 'MMMM d, yyyy')} • {lesson.startTime} - {lesson.endTime}
        </p>
      </div>

      <div className="space-y-2 text-sm">
        <div>
          <span className="font-medium">Type:</span>{' '}
          <span className="capitalize">{lesson.lessonType}</span>
        </div>

        <div>
          <span className="font-medium">Coaches:</span> {coachNames}
        </div>

        <div>
          <span className="font-medium">Court{occurrence.courtLabel?.includes(',') ? 's' : ''}:</span>{' '}
          {occurrence.courtLabel ?? (lesson.courtIds?.length ? `${lesson.courtIds.length} assigned` : '—')}
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-medium flex items-center gap-1">
              <Users className="h-3 w-3" />
              Enrollment
            </span>
            <span className={isFull ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
              {currentEnrollment}/{maxCapacity === -1 ? '∞' : maxCapacity}
            </span>
          </div>
          {maxCapacity !== -1 && (
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  isFull
                    ? 'bg-red-500'
                    : enrollmentPercentage > 75
                    ? 'bg-amber-500'
                    : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(enrollmentPercentage, 100)}%` }}
              />
            </div>
          )}
        </div>

        <div>
          <span className="font-medium">Status:</span>{' '}
          <span className="capitalize">{status}</span>
        </div>

        {status === 'cancelled' && (
          <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="text-xs">This lesson occurrence has been cancelled</span>
          </div>
        )}

        {status === 'postponed' && (
          <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="text-xs">This lesson has been postponed to {occurrence.override?.newDate && format(new Date(occurrence.override.newDate), 'MMM d, yyyy')}</span>
          </div>
        )}

        {!hasCoach && (
          <div className="flex items-start gap-2 text-orange-600 dark:text-orange-400">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="text-xs">No coach assigned</span>
          </div>
        )}

        {isFull && (
          <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="text-xs">Lesson is at full capacity</span>
          </div>
        )}

        {hasCoachAvailabilityWarning && (
          <div className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="text-xs">Coach is not available during this time slot</span>
          </div>
        )}

        {lesson.academyId && (
          <LessonWaitlistSection lessonId={lesson.id} academyId={lesson.academyId} />
        )}
      </div>
    </div>
  );
}
